import type { DehydratedState } from '@tanstack/react-query';

/**
 * Re-stamp a dehydrated cache with the moment the browser received it.
 *
 * `dehydrate()` serialises each query's server-side `dataUpdatedAt` and
 * `hydrate()` restores it verbatim. Listing routes are CDN-cached (up to
 * `s-maxage=3600` on /motion-graphics and /luts), so by the time the document
 * reaches a browser its hydrated entries can already be older than any sane
 * `staleTime`. Every grid then refetches on mount the exact rows that arrived
 * in the payload (DEV-6490).
 *
 * The payload is precisely as fresh as the document carrying it, so age it from
 * delivery. `dehydratedAt` moves with `dataUpdatedAt` to keep `hydrate`'s
 * newer-data comparisons consistent.
 *
 * Browser-only by contract — nothing here inspects the runtime, so callers must
 * gate it themselves (`_app` does, on `typeof window`). Re-stamping during the
 * server pass buys nothing (render time and fetch time are the same instant)
 * and would desync the server and client timestamps.
 */
export const rebaseHydratedState = (
  state: DehydratedState | undefined,
  receivedAt: number = Date.now()
): DehydratedState | undefined => {
  if (!state?.queries?.length) {
    return state;
  }

  return {
    ...state,
    queries: state.queries.map((query) => ({
      ...query,
      dehydratedAt: receivedAt,
      state: { ...query.state, dataUpdatedAt: receivedAt },
    })),
  };
};

/**
 * Apply {@link rebaseHydratedState} to a page's props, leaving them untouched
 * when there is nothing hydrated to re-stamp.
 */
export const withDeliveredHydrationTimestamps = <
  T extends { dehydratedState?: DehydratedState },
>(
  pageProps: T,
  receivedAt: number = Date.now()
): T => {
  const rebased = rebaseHydratedState(pageProps?.dehydratedState, receivedAt);

  if (rebased === pageProps?.dehydratedState) {
    return pageProps;
  }

  return { ...pageProps, dehydratedState: rebased };
};
